import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { jsPDF } from 'jspdf';

const DEFAULT_LOGO = '/clenip-logo.png';

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function imageUrlToPng(src, maxW = 400) {
  const img = await loadImage(src);
  const natW = img.naturalWidth || maxW;
  const natH = img.naturalHeight || maxW;
  const scale = Math.min(1, maxW / natW);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(natW * scale);
  canvas.height = Math.round(natH * scale);
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/png');
}

export default function CotizacionPreview({ cotizacion, onClose, formatCurrency }) {
  const [items, setItems] = useState([]);
  const [numero, setNumero] = useState('');
  const [fecha, setFecha] = useState(() => new Date().toISOString().split('T')[0]);
  const [cliente, setCliente] = useState('');
  const [numeroCliente, setNumeroCliente] = useState('');
  const [vendedorNombre, setVendedorNombre] = useState('');
  const [vendedorNumero, setVendedorNumero] = useState('');
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const [pageSize, setPageSize] = useState('letter');
  const [generating, setGenerating] = useState(false);
  const fileInputRef = useRef(null);

  // Inicializar items y resetear campos manuales cuando llega una nueva cotización
  useEffect(() => {
    if (cotizacion && cotizacion.breakdown) {
      setItems(cotizacion.breakdown.map((b, i) => ({
        id: `${Date.now()}-${i}`,
        descripcion: b.name,
        cantidad: 1,
        precio: Math.round(b.price),
      })));
      setNumero('');
      setCliente('');
      setNumeroCliente('');
      setVendedorNombre('');
      setVendedorNumero('');
      setFecha(new Date().toISOString().split('T')[0]);
    }
  }, [cotizacion]);

  // Cargar logo por defecto (normalizado a PNG via canvas)
  useEffect(() => {
    let cancelled = false;
    imageUrlToPng(DEFAULT_LOGO, 400)
      .then(d => { if (!cancelled) setLogoDataUrl(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!cotizacion) return null;

  const total = items.reduce((s, it) => s + (Number(it.cantidad) || 0) * (Number(it.precio) || 0), 0);

  const updateItem = (id, field, value) =>
    setItems(prev => prev.map(it => (it.id === id ? { ...it, [field]: value } : it)));
  const deleteItem = (id) => setItems(prev => prev.filter(it => it.id !== id));
  const addItem = () =>
    setItems(prev => [...prev, { id: `${Date.now()}-${Math.random()}`, descripcion: '', cantidad: 1, precio: 0 }]);

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await fileToDataUrl(file);
      setLogoDataUrl(dataUrl);
    } catch { /* ignore */ }
  };

  const fmtFecha = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  };

  const generarPDF = async () => {
    setGenerating(true);
    try {
      const unit = 'pt';
      const doc = new jsPDF({ orientation: 'portrait', unit, format: pageSize });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 40;
      let y = margin;

      // ---- Logo ----
      if (logoDataUrl) {
        try {
          const img = await loadImage(logoDataUrl);
          const maxW = 150;
          const ratio = img.naturalHeight / img.naturalWidth || 0.5;
          const w = maxW;
          const h = Math.min(maxW * ratio, 90);
          const fmt = logoDataUrl.includes('image/png') ? 'PNG' : 'JPEG';
          doc.addImage(logoDataUrl, fmt, margin, y, w, h);
        } catch { /* skip logo */ }
      }

      // ---- Datos de la cotización (derecha) ----
      const rightX = pageW - margin;
      doc.setFontSize(18);
      doc.setTextColor(40, 40, 90);
      doc.setFont('helvetica', 'bold');
      doc.text('COTIZACIÓN', rightX, y + 14, { align: 'right' });
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      const infoLines = [
        `N°: ${numero || '-'}`,
        `Fecha: ${fmtFecha(fecha)}`,
        `Cliente: ${cliente || '-'}`,
        `Contacto: ${numeroCliente || '-'}`,
      ];
      infoLines.forEach((line, i) => {
        doc.text(line, rightX, y + 34 + i * 15, { align: 'right' });
      });

      y = Math.max(y + 100, y + 34 + infoLines.length * 15);
      y += 10;

      // ---- Encabezado tabla ----
      const colDesc = margin;
      const colCant = pageW - margin - 260;
      const colUnit = pageW - margin - 170;
      const colSub = pageW - margin;
      const descWidth = colCant - colDesc - 10;

      doc.setDrawColor(79, 70, 229);
      doc.setLineWidth(1);
      doc.line(margin, y, pageW - margin, y);
      y += 14;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(79, 70, 229);
      doc.text('DESCRIPCIÓN', colDesc, y);
      doc.text('CANT.', colCant, y, { align: 'right' });
      doc.text('V. UNIT.', colUnit, y, { align: 'right' });
      doc.text('SUBTOTAL', colSub, y, { align: 'right' });
      y += 6;
      doc.setDrawColor(210, 210, 210);
      doc.setLineWidth(0.5);
      doc.line(margin, y, pageW - margin, y);
      y += 14;

      // ---- Filas ----
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(50, 50, 50);
      items.forEach((it) => {
        const cant = Number(it.cantidad) || 0;
        const precio = Number(it.precio) || 0;
        const sub = cant * precio;
        const descLines = doc.splitTextToSize(it.descripcion || '-', descWidth);
        const rowH = Math.max(descLines.length * 11, 12);

        if (y + rowH > pageH - margin - 80) {
          doc.addPage();
          y = margin;
        }

        doc.text(descLines, colDesc, y);
        doc.text(String(cant), colCant, y, { align: 'right' });
        doc.text(formatCurrency(precio), colUnit, y, { align: 'right' });
        doc.text(formatCurrency(sub), colSub, y, { align: 'right' });
        y += rowH + 6;
      });

      // ---- Total ----
      doc.setDrawColor(210, 210, 210);
      doc.line(margin, y, pageW - margin, y);
      y += 20;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(40, 40, 90);
      doc.text('TOTAL:', colUnit, y, { align: 'right' });
      doc.text(formatCurrency(total), colSub, y, { align: 'right' });

      // ---- Vendedor ----
      y += 40;
      if (y > pageH - margin - 40) { doc.addPage(); y = margin; }
      doc.setDrawColor(210, 210, 210);
      doc.setLineWidth(0.5);
      doc.line(margin, y, pageW - margin, y);
      y += 16;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(79, 70, 229);
      doc.text('VENDEDOR', margin, y);
      y += 15;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(60, 60, 60);
      doc.text(`Nombre: ${vendedorNombre || '-'}`, margin, y);
      doc.text(`Contacto: ${vendedorNumero || '-'}`, margin + 250, y);

      const nombreArchivo = `Cotizacion_${(numero || 'CLENIP').replace(/[^\w-]/g, '')}.pdf`;
      doc.save(nombreArchivo);
    } finally {
      setGenerating(false);
    }
  };

  const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-indigo-500 focus:outline-none';

  return ReactDOM.createPortal((
    <div
      className="fixed inset-0 z-[2000] bg-black/70 flex items-start justify-center p-4 overflow-y-auto"
      data-testid="cotizacion-preview-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl my-6" data-testid="cotizacion-preview">
        {/* Barra superior */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl z-10">
          <h3 className="text-lg font-bold text-gray-800">Vista previa de cotización</h3>
          <button
            onClick={onClose}
            data-testid="cotizacion-close-btn"
            className="w-9 h-9 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 flex items-center justify-center transition-colors"
          >✕</button>
        </div>

        {/* Cuerpo editable */}
        <div className="p-6 space-y-6">
          {/* Encabezado: logo + datos */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-3 flex items-center justify-center bg-gray-50 min-h-[110px]">
                {logoDataUrl
                  ? <img src={logoDataUrl} alt="Logo" className="max-h-24 object-contain" data-testid="cotizacion-logo-img" />
                  : <span className="text-gray-400 text-sm">Sin logo</span>}
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleLogoChange} className="hidden" data-testid="cotizacion-logo-input" />
              <button
                onClick={() => fileInputRef.current?.click()}
                data-testid="cotizacion-change-logo-btn"
                className="mt-2 text-xs text-indigo-600 hover:text-indigo-800 font-semibold"
              >Cambiar imagen</button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">N° de cotización</label>
                <input className={inputCls} value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="Ej: 001" data-testid="cotizacion-numero" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Fecha de cotización</label>
                <input type="date" className={inputCls} value={fecha} onChange={(e) => setFecha(e.target.value)} data-testid="cotizacion-fecha" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Nombre del cliente</label>
                <input className={inputCls} value={cliente} onChange={(e) => setCliente(e.target.value)} placeholder="Nombre" data-testid="cotizacion-cliente" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Número del cliente</label>
                <input className={inputCls} value={numeroCliente} onChange={(e) => setNumeroCliente(e.target.value)} placeholder="Teléfono" data-testid="cotizacion-numero-cliente" />
              </div>
            </div>
          </div>

          {/* Ítems */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-bold text-gray-700">Ítems</h4>
              <button onClick={addItem} data-testid="cotizacion-add-item" className="text-sm bg-indigo-600 text-white px-3 py-1.5 rounded-lg hover:bg-indigo-700 font-semibold">+ Agregar ítem</button>
            </div>
            <div className="overflow-x-auto border border-gray-200 rounded-xl">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 text-xs uppercase">
                    <th className="text-left p-2">Descripción</th>
                    <th className="text-right p-2 w-20">Cant.</th>
                    <th className="text-right p-2 w-28">V. Unit.</th>
                    <th className="text-right p-2 w-28">Subtotal</th>
                    <th className="p-2 w-10"></th>
                  </tr>
                </thead>
                <tbody data-testid="cotizacion-items">
                  {items.map((it) => (
                    <tr key={it.id} className="border-t border-gray-100">
                      <td className="p-2">
                        <input className="w-full px-2 py-1 border border-gray-200 rounded text-sm" value={it.descripcion} onChange={(e) => updateItem(it.id, 'descripcion', e.target.value)} data-testid="cotizacion-item-desc" />
                      </td>
                      <td className="p-2">
                        <input type="number" step="1" min="0" className="w-full px-2 py-1 border border-gray-200 rounded text-sm text-right" value={it.cantidad} onChange={(e) => updateItem(it.id, 'cantidad', e.target.value)} data-testid="cotizacion-item-cant" />
                      </td>
                      <td className="p-2">
                        <input type="number" step="1" min="0" className="w-full px-2 py-1 border border-gray-200 rounded text-sm text-right" value={it.precio} onChange={(e) => updateItem(it.id, 'precio', e.target.value)} data-testid="cotizacion-item-precio" />
                      </td>
                      <td className="p-2 text-right font-medium text-gray-700 whitespace-nowrap">
                        {formatCurrency((Number(it.cantidad) || 0) * (Number(it.precio) || 0))}
                      </td>
                      <td className="p-2 text-center">
                        <button onClick={() => deleteItem(it.id)} data-testid="cotizacion-item-delete" className="text-red-500 hover:text-red-700">🗑️</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end mt-3">
              <div className="text-right">
                <span className="text-sm text-gray-500 mr-3">TOTAL</span>
                <span className="text-2xl font-extrabold text-indigo-700" data-testid="cotizacion-total">{formatCurrency(total)}</span>
              </div>
            </div>
          </div>

          {/* Vendedor */}
          <div className="grid md:grid-cols-2 gap-4 border-t border-gray-200 pt-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Nombre del vendedor</label>
              <input className={inputCls} value={vendedorNombre} onChange={(e) => setVendedorNombre(e.target.value)} placeholder="Nombre del vendedor" data-testid="cotizacion-vendedor-nombre" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Número del vendedor</label>
              <input className={inputCls} value={vendedorNumero} onChange={(e) => setVendedorNumero(e.target.value)} placeholder="Teléfono del vendedor" data-testid="cotizacion-vendedor-numero" />
            </div>
          </div>

          {/* Tamaño de hoja */}
          <div className="flex items-center gap-3">
            <label className="text-sm font-semibold text-gray-600">Tamaño de hoja:</label>
            <select value={pageSize} onChange={(e) => setPageSize(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="cotizacion-pagesize">
              <option value="letter">Carta (Letter)</option>
              <option value="a4">A4</option>
            </select>
          </div>
        </div>

        {/* Botón descargar */}
        <div className="px-6 pb-6">
          <button
            onClick={generarPDF}
            disabled={generating}
            data-testid="cotizacion-download-pdf"
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 rounded-2xl font-bold text-lg hover:scale-[1.02] transition-transform shadow-lg disabled:opacity-60 disabled:scale-100"
          >
            {generating ? 'Generando PDF…' : '⬇️  Descargar cotización en PDF'}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}
